import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError, gbp } from "@/lib/api";
import { Field } from "./InvoiceForm";

export const SOR_STATUSES = ["Active", "Sold", "Withdrawn", "Returned"];
const NUMBER_FIELDS = ["mileage", "advertised_price", "minimum_return", "fee_value", "prep_costs", "withdrawal_fee", "term_days", "payout_days", "outstanding_finance", "sale_price"];
const OPTIONAL_INTS = ["mileage", "term_days", "payout_days"];

export const EMPTY_SOR = {
  agreement_date: new Date().toISOString().slice(0, 10), status: "Active", vehicle_id: "", owner_type: "private", owner_name: "", owner_address: "",
  owner_postcode: "", owner_phone: "", owner_email: "", registration: "", vin: "", make: "", model: "", derivative: "", colour: "", engine_no: "",
  first_registration: "", mileage: "", mot_expiry: "", advertised_price: "", minimum_return: "", fee_type: "fixed", fee_value: "", prep_costs: "",
  withdrawal_fee: "", term_days: "", end_date: "", payout_days: "", outstanding_finance: "", finance_company: "", insurance: "", test_drive_limit: "",
  agent_representative: "", sale_date: "", sale_price: "", notes: "",
};

export const sorFromRecord = (r) => Object.fromEntries(Object.entries({ ...EMPTY_SOR, ...r }).map(([k, v]) => [k, NUMBER_FIELDS.includes(k) && !v ? "" : v ?? ""]));

const FEE_TYPES = [["fixed", "Fixed fee (£)"], ["commission", "Commission (% of sale price)"], ["excess", "Everything above the owner's minimum return"]];
const sections = (f) => [
  ["Agreement", [["agreement_date", "Date of agreement", "date"], ["status", "Status", "select", SOR_STATUSES], ["agent_representative", "Our representative"]]],
  ["Owner", [["owner_type", "Owner type", "select", [["private", "Private individual"], ["business", "Business"]]], ["owner_name", "Full name / company name"],
    ["owner_address", "Address (one line per row)", "textarea"], ["owner_postcode", "Postcode"], ["owner_phone", "Telephone"], ["owner_email", "Email"]]],
  ["Vehicle", [["registration", "Registration"], ["vin", "VIN"], ["make", "Make"], ["model", "Model"], ["derivative", "Derivative / trim"], ["colour", "Colour"],
    ["engine_no", "Engine no."], ["mileage", "Mileage on receipt", "number"], ["first_registration", "Date of first registration", "date"], ["mot_expiry", "MOT expiry", "date"]]],
  ["Sale terms", [["advertised_price", "Advertised price (£)", "number"], ["minimum_return", "Owner's minimum net return (£)", "number"],
    ["fee_type", "Our fee basis", "select", FEE_TYPES], ...(f.fee_type === "excess" ? [] : [["fee_value", f.fee_type === "commission" ? "Commission (%)" : "Fixed fee (£)", "number"]]),
    ["prep_costs", "Agreed preparation / valeting costs (£)", "number"], ["withdrawal_fee", "Withdrawal fee (£)", "number"], ["term_days", "Agreement period (days)", "number"],
    ["end_date", "End date", "date"], ["payout_days", "Pay owner within (working days)", "number"],
    ["insurance", "Insurance while with us", "select", [["", "Not stated"], ["agent", "Our motor trade policy"], ["owner", "Owner's own policy"]]], ["test_drive_limit", "Test drive mileage limit"]]],
  ["Finance", [["outstanding_finance", "Outstanding finance to settle from proceeds (£)", "number"], ["finance_company", "Finance company and agreement no."]]],
  ["Sale (complete when sold)", [["sale_date", "Date of sale", "date"], ["sale_price", "Sale price (£)", "number"]]],
  ["Notes", [["notes", "Internal notes (not printed)", "textarea"]]],
];

export const sorFee = (f) => {
  const sale = Number(f.sale_price) || 0;
  if (!sale) return 0;
  if (f.fee_type === "commission") return Math.round(sale * (Number(f.fee_value) || 0)) / 100;
  if (f.fee_type === "fixed") return Number(f.fee_value) || 0;
  return Math.max(0, sale - (Number(f.minimum_return) || 0) - (Number(f.prep_costs) || 0) - (Number(f.outstanding_finance) || 0));
};
export const sorNet = (f) => (Number(f.sale_price) || 0) - sorFee(f) - (Number(f.prep_costs) || 0) - (Number(f.outstanding_finance) || 0);

const toBody = (f) => Object.fromEntries(Object.entries(f).map(([k, v]) => [k, NUMBER_FIELDS.includes(k) ? (v === "" ? (OPTIONAL_INTS.includes(k) ? null : 0) : Number(v)) : v]));

const fromVehicle = (v) => ({
  vehicle_id: v.id, registration: v.registration_number || "", vin: v.vin || "", make: v.make || "", model: v.model || "", derivative: v.derivative || "",
  colour: v.colour || "", mileage: v.mileage ?? "", advertised_price: v.price ?? "", mot_expiry: /^\d{4}-\d{2}-\d{2}$/.test(v.mot_expiry || "") ? v.mot_expiry : "",
});

export function SorForm({ initial, sorNo, onSaved, onCancel }) {
  const [f, setF] = useState(initial);
  const [vehicles, setVehicles] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => {
    api.get("/admin/vehicles").then((r) => setVehicles(Array.isArray(r.data) ? r.data : [])).catch(() => setVehicles([]));
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.owner_name.trim() || !f.model.trim() || !(Number(f.minimum_return) > 0)) return toast.error("Owner name, model and the owner's minimum return are required");
    setBusy(true);
    try {
      const { data } = sorNo ? await api.put(`/admin/sor/${sorNo}`, toBody(f)) : await api.post("/admin/sor", toBody(f));
      toast.success(`SOR agreement ${data.sor_no} saved`);
      onSaved(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="border border-stone bg-paper p-6" data-testid="sor-form">
      <h2 className="mb-5 font-serif text-2xl">{sorNo ? `Edit SOR ${sorNo}` : "Record an SOR"}</h2>
      <label htmlFor="sor-vehicle-pick" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.1em] text-charcoal/75">Fill vehicle details from stock (optional)</label>
      <select id="sor-vehicle-pick" className="field mb-6 !py-2.5 text-sm" data-testid="sor-vehicle-pick" value={f.vehicle_id}
        onChange={(e) => { const v = vehicles.find((x) => x.id === e.target.value); setF((p) => (v ? { ...p, ...fromVehicle(v) } : { ...p, vehicle_id: "" })); }}>
        <option value="">Not in stock list / enter manually</option>
        {vehicles.map((v) => <option key={v.id} value={v.id}>{[v.year, v.make, v.model, v.derivative].filter(Boolean).join(" ")}</option>)}
      </select>
      {sections(f).map(([title, fields]) => (
        <fieldset key={title} className="mb-6">
          <legend className="mb-3 text-sm font-semibold text-forest">{title}</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            {fields.map(([k, l, t, o]) => <Field key={k} k={k} label={l} type={t} options={o} value={f[k]} onChange={set(k)} prefix="sor" />)}
          </div>
        </fieldset>
      ))}
      {Number(f.sale_price) > 0 && (
        <p className="mb-6 border border-stone bg-stone-light p-4 text-sm" data-testid="sor-settlement">
          Our fee {gbp(sorFee(f))} · costs {gbp(Number(f.prep_costs) || 0)} · finance {gbp(Number(f.outstanding_finance) || 0)} · <strong>owner receives {gbp(sorNet(f))}</strong>
          {sorNet(f) < (Number(f.minimum_return) || 0) && <span className="mt-1 block text-red-800">Below the owner's minimum net return: get their consent first.</span>}
        </p>
      )}
      <div className="flex flex-wrap gap-3">
        <button type="submit" disabled={busy} className="btn btn-primary !py-3" data-testid="sor-save">{busy ? "Saving…" : sorNo ? "Save changes" : "Save SOR"}</button>
        <button type="button" onClick={onCancel} className="btn !py-3" data-testid="sor-cancel">Cancel</button>
      </div>
    </form>
  );
}
